/*
 * Voyado card prices — per-market correction via the Shopify Storefront API.
 * ------------------------------------------------------------------------
 * The Voyado feed price is market-scoped (uk/row/us) and NOT currency-converted, so on any
 * converted-currency market (e.g. Brazil) the feed value itself is wrong for that market. Note that
 * Shopify.formatMoney is not loaded in this theme; the formatter below is the theme's own.
 *
 * Mirrors Nobody's Child: after Voyado renders cards, batch-fetch the correct per-market price
 * from the Storefront API with @inContext(country:) — amount + currencyCode already converted by
 * Shopify Markets — and swap it into the card. This is the STOREFRONT API (public, client-side),
 * not Admin GraphQL. Token/domain/version come from window.broraStorefront; country from
 * window.broraVoyado.country.
 *
 * Runs on EVERY market with a country (ENABLED = !!country): the fetched Storefront price is the single
 * source of truth for the card price on all markets, base included. Only the wrong-currency BLANK (no flash)
 * and the price-range filter rescale are gated on a converted currency (CONVERTED, rate !== 1) — the base
 * market (GBP, rate 1.0) is already exact, so it's neither blanked nor rescaled. On any failure it restores
 * the Voyado-rendered price, so a missing token / blocked request / single-market store degrades gracefully.
 *
 * Hooks: listens for `voyado:cards-rendered`; reads [data-wishlist-product-id] (= Shopify product
 * id) and writes [data-slot="price"] / [data-slot="compare"] per card.
 */
(function () {
  'use strict';

  const cfg = window.broraVoyado || {};
  const country = cfg.country || null;
  const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
  // Fetch Storefront prices on EVERY market (single source of truth, mirrors Nobody's Child) so cards
  // always show the current per-market price, not Voyado's feed price.
  const ENABLED = !!country;
  // Only the Elevate base market carries the real figures: there the card keeps its number (no flash)
  // and the filter needs no rescale. Membership decides that, not the rate — a GBP market outside it
  // is still on the Elevate scale.
  const CONVERTED = !!country && (cfg.market !== 'uk' || (rate && rate !== 1));

  // Voyado's row-market price is ~proportional to the real price (a stable per-store scale factor).
  // We collect voyadoMin/realMin per card and expose the median as window.broraVoyado.priceScale so
  // the price-range filter can rescale its (Voyado-scale) slider bounds into the real market currency
  // for DISPLAY while its query keeps working on the Voyado scale. Accumulates across renders.
  const factors = [];

  function publishScale() {
    if (!factors.length) return;
    const sorted = factors.slice().sort(function (a, b) { return a - b; });
    const median = sorted[Math.floor(sorted.length / 2)];
    if (!(median > 0)) return;
    window.broraVoyado.priceScale = median;
    window.broraVoyado.currencyCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
    document.dispatchEvent(new CustomEvent('voyado:price-scale', { detail: { scale: median } }));
  }

  // Storefront API credentials, served by layout/theme.liquid.
  function storefrontConfig() {
    const c = window.broraStorefront;
    return (c && c.token && c.domain && c.version) ? c : null;
  }

  // Formatter derived from the market's OWN money format (window.broraVoyado.moneyFormat, e.g.
  // "R$ 111.222,33" / "£111,222.33") so Voyado prices match the native Shopify prices exactly on
  // every market. Falls back to Intl by currency code. Exposed as window.broraVoyado.formatMoney so
  // the price-range filter reuses the same formatting.
  const format = (function () {
    const sample = (window.broraVoyado && window.broraVoyado.moneyFormat) || '';
    const m = sample.match(/\d[\d.,  ]*\d|\d/);
    let prefix = '', suffix = '', thou = '', dec = '', decimals = 0, ok = false;
    if (m) {
      const num = m[0], at = sample.indexOf(num);
      prefix = sample.slice(0, at); suffix = sample.slice(at + num.length);
      const last = Math.max(num.lastIndexOf('.'), num.lastIndexOf(','));
      if (last > -1 && num.length - last - 1 === 2) { dec = num[last]; decimals = 2; }
      // The grouping character is read from the sample rather than inferred from the decimal one, so the
      // space-separated formats (CVE, UZS, XPF) group like their Liquid counterpart.
      const groups = num.replace(/\d/g, '').split('').filter(function (c) { return c !== dec; });
      thou = groups.length ? groups[0] : '';
      ok = true;
    }
    // Append the ISO code where the checkout (ICU narrowSymbol) prints the code rather than a glyph, plus
    // a bare "$" (USD/CAD disambiguation), unless the market format already carries that code. Mirrors
    // snippets/money.liquid.
    const bareDollar = (prefix + suffix).trim() === '$';
    // Currencies whose market money format shows a different symbol from the checkout, as
    // [marketAffix, checkoutPrefix, checkoutSuffix]: the market affix is dropped and the checkout one
    // applied, leaving the market's own digit grouping untouched. A blank prefix and suffix means the
    // checkout shows the ISO code alone. Twin of `checkout_symbols` in snippets/money.liquid — keep
    // both maps in sync.
    const checkout_symbols = {"ALL":["Lek ","ALL ",""],"AMD":[" AMD","֏",""],"AWG":["Afl","AWG ",""],"AZN":["m.","₼",""],"BDT":["Tk ","৳",""],"BSD":["BS$","$",""],"BZD":["BZ$","$",""],"CVE":["$","CVE ",""],"DJF":[" Fdj","DJF ",""],"DOP":["RD$ ","$",""],"DZD":["DA ","DZD ",""],"EGP":["LE ","E£",""],"ETB":["Br","ETB ",""],"FJD":["$","$",""],"GEL":[" GEL",""," ₾"],"GYD":["G$","$",""],"HKD":["$","$",""],"HTG":["G ","HTG ",""],"ILS":[" NIS","₪",""],"INR":["Rs. ","₹",""],"KES":["KSh","Ksh ",""],"KGS":["лв","⃀",""],"KHR":["KHR","៛",""],"LRD":["L$","L$",""],"LSL":["M","LSL ",""],"MAD":[" dh","MAD ",""],"MDL":[" MDL","MDL ",""],"MKD":["ден ","MKD ",""],"MOP":["MOP$","P$",""],"MWK":["Mk","MK ",""],"MZN":[" Mt","MZN ",""],"NAD":["N$","$",""],"PEN":["S/. ","PEN ",""],"PKR":["Rs.","Rs ",""],"PYG":["Gs. ","₲",""],"RSD":[" RSD","RSD ",""],"SAR":[" SR","SAR ",""],"SCR":["Rs ","SR ",""],"STD":["Db ","STD ",""],"SZL":["L ","E ",""],"TOP":["$","T$",""],"TZS":[" TZS","TSh ",""],"UYU":["$","$",""],"UZS":[" som","UZS ",""],"VUV":["$","VT ",""],"WST":["WS$ ","$",""],"XOF":["CFA","F CFA ",""],"XPF":[" XPF","CFPF ",""],"ZMW":["K","ZK ",""]};
    // Mirrors the `code_currencies` list in snippets/money.liquid.
    const code_currencies = 'AED,ALL,AWG,BIF,CDF,CHF,CVE,DJF,DZD,ETB,GMD,HTG,KES,LSL,MAD,MDL,MKD,MVR,MWK,MZN,PEN,PGK,QAR,RSD,SAR,SCR,SLL,STD,SZL,TZS,UGX,UZS,VUV,WST'.split(',');
    const cldrLang = 'en-GB'; // pinned so the code-fallback set is deterministic, not visitor-ICU-dependent
    const codeFallbackCache = {};
    function checkoutShowsCode(code) {
      if (!code) return false;
      if (code in codeFallbackCache) return codeFallbackCache[code];
      let r;
      try { r = new Intl.NumberFormat(cldrLang, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).format(0).indexOf(code) !== -1; }
      catch (e) { r = false; }
      codeFallbackCache[code] = r;
      return r;
    }
    // noDecimals: force whole-number output (the price-range facet passes it for its whole bounds).
    function fmt(amount, currencyCode, noDecimals) {
      const n = Number(amount);
      if (!isFinite(n)) return null;
      const withDecimals = decimals && !noDecimals;
      // The code is appended where the symbol cannot stand alone: absent, or an ambiguous "$".
      const override = currencyCode && checkout_symbols[currencyCode];
      const overrideSymbol = override ? (override[1] + override[2]).trim() : '';
      const showCode = currencyCode && (override
        ? (overrideSymbol === '' || overrideSymbol.indexOf('$') !== -1 || code_currencies.indexOf(currencyCode) !== -1)
        : (bareDollar || checkoutShowsCode(currencyCode)));
      if (ok) {
        const str = withDecimals ? n.toFixed(2) : String(Math.round(n));
        const parts = str.split('.');
        let ip = parts[0];
        if (thou) ip = ip.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
        const num = ip + (withDecimals ? dec + parts[1] : '');
        const base = override ? override[1] + num + override[2] : prefix + num + suffix;
        return showCode && base.indexOf(currencyCode) === -1 ? base + ' ' + currencyCode : base;
      }
      try {
        return new Intl.NumberFormat(cldrLang, { style: 'currency', currency: currencyCode, minimumFractionDigits: noDecimals ? 0 : undefined, maximumFractionDigits: noDecimals ? 0 : undefined }).format(n);
      } catch (e) { return null; }
    }

    // Inverse of the formatter. The market's format says which separator is the decimal one: reading
    // digits and dots alone turns "Dhs. 4,480.00" into 0.448 and "R$ 3.517,00" into 3.517.
    fmt.parse = function (text) {
      const run = String(text == null ? '' : text).match(/\d[\d.,'\u00a0\u202f ]*\d|\d/);
      if (!run) return NaN;
      const group = ok ? thou : ',';
      const point = ok ? dec : '.';
      let num = run[0];
      if (group) num = num.split(group).join('');
      // Apostrophe and the non-breaking spaces only ever group digits.
      num = num.replace(/['\u00a0\u202f ]/g, '');
      if (point && point !== '.') num = num.split(point).join('.');
      return parseFloat(num);
    };

    return fmt;
  })();

  // Dirham glyph for the AED market — mirrors snippets/icon-dirham.liquid. Injected via innerHTML (static
  // markup + a numeric amount, no user input). formatMoneyHtml returns the glyph HTML on AED, else the plain
  // formatted string. Migrate alongside the Liquid snippet once Unicode 18.0 (U+20C3) has font support.
  var DIRHAM_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 324.19 290.45" style="height:8.5px;width:auto;fill:currentColor;transform:translateY(-0.5px)" aria-hidden="true" focusable="false"><path d="M324.19,196l-11.564-6.936-35.262.109c-4.33,19.185-12.015,36.87-24.215,52.285-52.205,65.958-150.381,44.876-223.958,48.532,2.849-4.499,5.761-8.708,7.763-13.727,1.821-4.565,5.237-16.258,5.237-20.763v-67.5c-.733-.266-1.196,1-1.5,1h-17c-.439,0-5.271-1.443-6.211-1.789C3.818,182.177-1.094,165.349.2,152c2.935,2.677,10.64,8,14.49,8h27.5v-29h-19.5c-2.219,0-9.47-3.846-11.523-5.477C1.752,118.046-.644,105.426.2,94c2.717,2.386,10.046,8,13.49,8h28.5V35.5c0-3.807-3.689-16.654-5.239-20.761L29.199,0c1.487.774,2.749,1.006,4.438,1.053,53.694,1.484,121.79-8.11,171.229,12.77,37.763,15.949,63.968,47.84,72.42,88.078.965.826,21.558-.319,25.406.096,11.676,1.26,18.623,12.809,21.497,23.003v13c-2.88-1.425-8.525-7-11.5-7h-31.5c-.266.733,1,1.196,1,1.5v25c0,.35-1.774,1.109.5,2.5h22c1.226,0,6.431,3.176,7.826,4.174,6.946,4.973,8.988,11.078,11.674,18.826v13ZM229.19,102c-3.228-25.477-14.198-52.622-35.081-68.419-31.382-23.739-71.896-17.869-108.919-18.581v87h144ZM233.268,131H85.19v29h148.078v-29ZM227.69,189c-46.154,1.783-94.583,0-141,0-.304,0-.767-1.266-1.5-1v87c41.025.035,87.048,4.95,118.024-27.476,12.247-12.821,24.852-40.392,25.994-58.02.202-3.118-1.042-.523-1.518-.505Z"/></svg>';
  function formatHtml(amount, currencyCode, noDecimals) {
    var s = format(amount, currencyCode, noDecimals);
    if (s == null) return s;
    var active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || currencyCode;
    if (active !== 'AED') return s;
    return '<span style="display:inline-flex;align-items:baseline;gap:2px">' + DIRHAM_SVG + '<span>' + s.replace(/^[^\d]*/, '') + '</span></span>';
  }
  if (window.broraVoyado) {
    window.broraVoyado.formatMoney = format;
    window.broraVoyado.formatMoneyHtml = formatHtml;
  }

  async function fetchPrices(gids, api) {
    const query =
      'query($ids:[ID!]!, $country: CountryCode!) @inContext(country: $country) {' +
      '  nodes(ids:$ids){ ... on Product { id' +
      '    priceRange { minVariantPrice { amount currencyCode } }' +
      '    compareAtPriceRange { minVariantPrice { amount currencyCode } }' +
      '  } } }';
    const res = await fetch('https://' + api.domain + '/api/' + api.version + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': api.token
      },
      body: JSON.stringify({ query: query, variables: { ids: gids, country: country } })
    });
    if (!res.ok) throw new Error('storefront ' + res.status);
    const j = await res.json();
    const map = {};
    ((j.data && j.data.nodes) || []).forEach(function (n) { if (n && n.id) map[n.id] = n; });
    return map;
  }

  async function apply(root, opts) {
    if (!ENABLED) return;
    const skipScale = !!(opts && opts.skipScale); // recs correct price only, never drive the filter scale
    const api = storefrontConfig();
    if (!api) return;

    const scope = (root && root.querySelectorAll) ? root : document;
    // Only cards not already priced — the PLP grid fires cards-rendered with the WHOLE grid on each
    // infinite-scroll page, so without this we'd re-blank + re-fetch every product on every page.
    // [data-voyado-priceable] = non-card surfaces that reuse this correction (mini-bag recs) via
    // window.broraVoyado.applyPrices; they carry the same [data-wishlist-product-id] + [data-slot="price"].
    const cards = Array.prototype.slice.call(scope.querySelectorAll('[data-voyado-card]:not([data-voyado-priced]), [data-voyado-priceable]:not([data-voyado-priced])'));
    const byGid = new Map(); // gid → [card,…]
    const restore = []; // [{el, text}] to restore on failure

    cards.forEach(function (card) {
      const idEl = card.querySelector('[data-wishlist-product-id]');
      const priceEl = card.querySelector('[data-slot="price"]');
      const id = idEl && idEl.getAttribute('data-wishlist-product-id');
      if (!id || !priceEl) return;
      card.setAttribute('data-voyado-priced', ''); // claim it so later renders (pagination) skip it
      const gid = 'gid://shopify/Product/' + id;
      if (!byGid.has(gid)) byGid.set(gid, []);
      byGid.get(gid).push(card);
      // Keep the original (fill fallback + restore-on-failure).
      restore.push({ el: priceEl, text: priceEl.textContent });
      if (CONVERTED) priceEl.textContent = "\u00a0"; // hide wrong-currency value; base price is already right
    });

    if (!byGid.size) return;

    let prices;
    try {
      prices = await fetchPrices(Array.from(byGid.keys()), api);
    } catch (e) {
      restore.forEach(function (r) { r.el.textContent = r.text; }); // degrade to Voyado price
      return;
    }

    byGid.forEach(function (cardsForGid, gid) {
      const node = prices[gid];
      const sp = node && node.priceRange && node.priceRange.minVariantPrice;
      cardsForGid.forEach(function (card) {
        const priceEl = card.querySelector('[data-slot="price"]');
        const compareEl = card.querySelector('[data-slot="compare"]');
        const orig = restore.find(function (x) { return x.el === priceEl; });
        if (priceEl) {
          // Scale factor (converted markets only, for the filter) = the Voyado-rendered number
          // (e.g. "£20000" → 20000) ÷ the real amount.
          if (CONVERTED && !skipScale && sp && orig) {
            const voyNum = format.parse(orig.text);
            const realNum = parseFloat(sp.amount);
            if (voyNum > 0 && realNum > 0) factors.push(voyNum / realNum);
          }
          const f = sp && formatHtml(sp.amount, sp.currencyCode);
          // A product the Storefront API does not return has no price of its own on this market, and
          // the Voyado number is the base currency's — on a converted market it would read as this
          // market's. The slot stays blank there; on the base market the feed's number is the right
          // one, so it goes back.
          if (f) priceEl.innerHTML = f;
          else if (!CONVERTED) priceEl.innerHTML = orig ? orig.text : priceEl.innerHTML;
        }
        if (compareEl) {
          // Per-market compare from the Storefront API — the Voyado feed carries no sale. The renderer
          // leaves this slot for us to fill, or to remove where there is none so the empty span doesn't
          // leave a gap in the flex price row.
          const cp = node && node.compareAtPriceRange && node.compareAtPriceRange.minVariantPrice;
          const f = cp && sp && Number(cp.amount) > Number(sp.amount) && formatHtml(cp.amount, cp.currencyCode);
          if (f) compareEl.innerHTML = f;
          else compareEl.remove();
        }
      });
    });

    if (CONVERTED && !skipScale) publishScale();
  }

  document.addEventListener('voyado:cards-rendered', function (e) {
    apply(e.detail && e.detail.root);
  });

  // Reusable hook for non-card surfaces (mini-bag recs) — correct their prices on demand, without
  // firing the global cards-rendered event (which other listeners key on) and without the filter scale.
  if (window.broraVoyado) window.broraVoyado.applyPrices = function (root) { return apply(root, { skipScale: true }); };
})();
